<script>
import brandingAPI from "@/services/branding/brandingAPI"
import CompanySSOAPI from "@/services/sso/company/companySSO.API"
import Swal from "sweetalert2"

export default {
  name: "BrandingSection",
  props: {
    companyId: { type: [Number, String], required: true },
    companyUuid: { type: [Number, String], required: true },
  },
  data() {
    return {
      sso: {
        commercial_name: "",
        legal_name: "",
        nit: "",
        phone: "",
        email: "",
      },
      ssoLoaded: false,
      ssoError: null,
      logoUrl: null,
      uploadingLogo: false,
      loading: true,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.loading = true
      await Promise.all([this.loadBranding(), this.pullAndSyncSso()])
      this.loading = false
    },

    async loadBranding() {
      try {
        const res = await brandingAPI.show(this.companyId)
        if (res && res.data) {
          this.logoUrl = res.logo_url
        }
      } catch (e) {
        // silencioso — la primera vez la fila no existe y el endpoint la crea
      }
    },

    async pullAndSyncSso() {
      try {
        const ssoData = await CompanySSOAPI.getCompany(this.companyUuid)
        if (!ssoData || !ssoData.id) {
          this.ssoError = "SSO no retornó datos para esta empresa"
          return
        }
        this.sso = {
          commercial_name: ssoData.social_reason || ssoData.name || "",
          legal_name: ssoData.legal_representative || "",
          nit: this.composeNit(ssoData.nit, ssoData.dv),
          phone: ssoData.phone || "",
          email: ssoData.legal_representative_email || ssoData.email || "",
        }
        this.ssoLoaded = true

        await brandingAPI.syncSso(this.companyId, this.sso)
      } catch (e) {
        console.error("Error sincronizando SSO:", e)
        this.ssoError = "No se pudieron obtener los datos del SSO"
      }
    },

    composeNit(nit, dv) {
      if (!nit) return ""
      return dv !== undefined && dv !== null && dv !== ""
        ? `${nit}-${dv}`
        : `${nit}`
    },

    async onLogoFile(event) {
      const file = event.target.files[0]
      if (!file) return
      this.uploadingLogo = true
      try {
        const res = await brandingAPI.uploadLogo(this.companyId, file)
        if (res && res.logo_url) {
          this.logoUrl = res.logo_url
          Swal.fire({
            icon: "success",
            title: "Logo actualizado",
            timer: 1500,
            showConfirmButton: false,
          })
        } else {
          Swal.fire("Error", "No se pudo subir el logo", "error")
        }
      } finally {
        this.uploadingLogo = false
        event.target.value = ""
      }
    },
  },
}
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title">Identidad corporativa</h5>
      <p class="text-muted small">
        Los datos legales se heredan automáticamente del sistema SSO.
        Aquí solo configuras el logo que aparecerá en los PDFs.
      </p>

      <div v-if="loading" class="text-center my-4">
        <b-spinner small variant="primary"></b-spinner>
      </div>

      <div v-else class="row">
        <div class="col-md-4 text-center">
          <div class="bs-logo-box">
            <img v-if="logoUrl" :src="logoUrl" alt="logo" />
            <div v-else class="bs-logo-empty">Sin logo</div>
          </div>
          <label class="btn btn-outline-primary btn-sm mt-2">
            <span v-if="uploadingLogo"><b-spinner small></b-spinner></span>
            <span v-else>📷 {{ logoUrl ? "Cambiar logo" : "Subir logo" }}</span>
            <input type="file" accept="image/png,image/jpeg" hidden @change="onLogoFile" />
          </label>
          <div class="text-muted small mt-1">PNG o JPG · max 2MB</div>
        </div>

        <div class="col-md-8">
          <div class="bs-sso-header d-flex align-items-center mb-2">
            <strong>Datos legales heredados del SSO</strong>
            <span v-if="ssoLoaded" class="badge badge-success ml-2">Sincronizado</span>
            <span v-else-if="ssoError" class="badge badge-warning ml-2">{{ ssoError }}</span>
            <span v-else class="badge badge-secondary ml-2">Cargando...</span>
          </div>

          <div class="form-group">
            <label>Nombre comercial / Razón social</label>
            <input :value="sso.commercial_name" class="form-control" readonly />
          </div>
          <div class="row">
            <div class="col-md-6 form-group">
              <label>NIT</label>
              <input :value="sso.nit" class="form-control" readonly />
            </div>
            <div class="col-md-6 form-group">
              <label>Teléfono</label>
              <input :value="sso.phone" class="form-control" readonly />
            </div>
          </div>
          <div class="form-group">
            <label>Representante legal</label>
            <input :value="sso.legal_name" class="form-control" readonly />
          </div>
          <div class="form-group">
            <label>Email del representante legal</label>
            <input :value="sso.email" class="form-control" readonly />
          </div>

          <p class="text-muted small mt-2 mb-0">
            <i class="bx bx-info-circle"></i>
            Para modificar estos datos, hazlo desde el módulo SSO de Emisión.
            Los cambios se reflejan automáticamente aquí al recargar.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bs-logo-box {
  border: 2px dashed #ddd; border-radius: 8px;
  min-height: 140px; display: flex; align-items: center; justify-content: center;
  background: #fafafa; padding: 10px;
}
.bs-logo-box img { max-width: 100%; max-height: 130px; }
.bs-logo-empty { color: #999; font-size: 0.85rem; }
.bs-sso-header { font-size: 0.9rem; }
input[readonly] { background: #f7f8fa; }
</style>
